<template>
	<div class="weather-mini">
		<div class="weather-container">
			<div class="conditions">
				<div class="condition-img">
					<img v-bind:src="currentIconPath" />
				</div>
				<div class="condition-text">
					<div class="condition-temp-desc">
						<div class="condition-temp">
							{{ currentTemp }}&deg;
						</div>
						<div class="condition-desc">
							{{ currentDescription }}
						</div>
					</div>
					<div class="condition-detail">
						<span class="realtemp" v-show="feelsLike != null">Feels Like {{ feelsLike }}&deg;</span>
						<span class="wind" v-show="windSpeed > 0">Wind {{ windDirection }} {{ windSpeed }}mph</span>
						<span class="wind" v-show="windSpeed <= 0">No Wind</span>
						<span class="humid" v-show="humidity > 0">Humidity {{ humidity }}%</span>
						<span class="sun" v-show="nextSunTime">{{ nextSunDescription }} {{ nextSunTime }}</span>
					</div>
				</div>
			</div>
			<div class="severe-alert" v-if="currentAlert != null">
				<strong>{{ currentAlert.longTextDescription }}</strong> until {{ currentAlert.expirationString }}
			</div>
			<div class="forecast">
				<div class="forecast-item" v-for="item in forecast">
					<h2>{{ item.dayAbbreviation }}</h2>
					<div class="forecast-detail">
						<div class="forecast-img">
							<img v-bind:src="item.forecastItemPath" />
						</div>
						<div class="forecast-temps">
							<span class="hi" v-if="item.highTemperature != null">{{ item.highTemperature }}&deg;</span>
							<span class="hi" style="text-align: center" v-else>&ndash;</span>
							<span class="lo">{{ item.lowTemperature }}&deg;</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import Bugsnag from '@bugsnag/js'

	let refreshConditionsInterval = null;
	let refreshForecastInterval = null;
	let refreshAlertsInterval = null;
	//let tornadoAlert = new Audio("sounds/tornado-hifi.wav");
	//let tstormAlert = new Audio("sounds/tstorm-hifi.wav");
	let weatherAlertChime = new Audio("sounds/weather-alert.mp3");

	export default {
		name: 'MiniWeather',
		data() {
			return {
				currentDescription: "Loading...",
				currentTemp: "— ",
				feelsLike: null,
				windDirection: null,
				windSpeed: null,
				sunrise: null,
				sunset: null,
				humidity: 0,
				nextSunDescription: null,
				nextSunTime: null,
				forecast: [],
				currentAlert: null,
				currentIcon: "rainbow"
			}
		},
		computed: {
			currentIconPath() {
				return "images/weather/" + this.currentIcon + ".svg"
			},
			currentIcon() {
				return this.getConditionIcon();
			}
		},

		mounted() {
			const self = this;
			console.log("Weather mini module initiated");
			this.updateConditions();
			this.updateForecast();
			this.checkAlerts();
			refreshConditionsInterval = setInterval(this.updateConditions, 1000*60*10);
			refreshForecastInterval = setInterval(this.updateForecast, 1000*60*10);
			refreshAlertsInterval = setInterval(this.checkAlerts, 1000 * 60 * 2);

			window.addEventListener('weatherAlert', (e) => {
				self.handleWeatherAlert(e.detail);
			});
		},

		destroyed() {
			const self = this;
			console.log("Stopping Weather mini module");
			clearInterval(refreshConditionsInterval);
			clearInterval(refreshForecastInterval);
			clearInterval(refreshAlertsInterval);

			window.removeEventListener('weatherAlert', (e) => {
				self.handleWeatherAlert(e.detail);
			});
		},

		methods: {

			updateConditions() {
				let self = this;
				this.$http.get('/weather/conditions').then(response => {
					let data = response.body;
					self.currentTemp = data.temperature != null ? data.temperature : "— ";
					if (data.heatIndex) {
						self.feelsLike = data.heatIndex;
					} else if (data.windChill) {
						self.feelsLike = data.windChill;
					} else {
						self.feelsLike = null;
					}
					self.windDirection = data.windDirection;
					self.windSpeed = data.windSpeed;
					self.sunrise = data.sunrise;
					self.sunset = data.sunset;
					self.nextSunDescription = data.nextSunDescription;
					self.nextSunTime = data.nextSunTime;
					self.humidity = data.humidity;
					self.currentDescription = data.description;
					self.currentIcon = data.icon;
				}).catch(e => {
					Bugsnag.notify(new Error(e.url), function (event) {
						event.addMetadata('httpResponse', e);
					});
					console.error(e);
				});
			},

			updateForecast() {
				let self = this;
				this.$http.get('/weather/forecast').then(response => {
					let forecast = response.body;
					self.forecast = [];
					forecast.forEach((f) => {
						f.forecastItemPath = "images/weather/" + f.icon + ".svg";
						self.forecast.push(f);
					});
				}).catch(e => {
					Bugsnag.notify(new Error(e.url), function (event) {
						event.addMetadata('httpResponse', e);
					});
					console.error(e);
				});
			},

			checkAlerts() {
				let self = this;
				this.$http.get('/weather/alerts').then(response => {
					let alerts = response.body;
					self.currentAlert = alerts.currentAlert;
				}).catch(e => {
					Bugsnag.notify(new Error(e.url), function (event) {
						event.addMetadata('httpResponse', e);
					});
					console.error(e);
				});
			},

			getConditionIcon() {

				if (this.currentAlert) {
					if (this.currentAlert.event === "Tornado Warning") return "tornado-warning";
					if (this.currentAlert.event === "Severe Thunderstorm Warning") return "tstorm-warning";
				}

				return this.currentIcon;
			},

			handleWeatherAlert(data) {
				console.log("Weather alert received: " + data.longTextDescription + " until " + data.longExpirationString);
				this.currentAlert = data;
				if (this.currentAlert.longTextDescription === "Tornado Warning") this.soundTornadoAlert();
				else if (this.currentAlert.longTextDescription === "Severe Thunderstorm Warning") this.soundTstormAlert();
			},

			speakAlert(text) {
				let audio = new Audio("/polly/speak?text=" + encodeURIComponent(text));
				audio.addEventListener("canplaythrough", () => {
					audio.play();
				});
			},

			soundTornadoAlert() {
				if (!window.audio) return;
				const self = this;
				weatherAlertChime.currentTime = 0;
				weatherAlertChime.play();
				setTimeout(function () {
					self.speakAlert("Alert from the National Weather Service. Tornado Warning. Seek shelter immediately!");
				}, 2000);
			},

			soundTstormAlert() {
				if (!window.audio) return;
				const self = this;
				weatherAlertChime.currentTime = 0;
				weatherAlertChime.play();
				setTimeout(function () {
					self.speakAlert("Alert from the National Weather Service. Severe Thunderstorm Warning.");
				}, 2000);
			}
		}
	}
</script>

<style lang="less">
	@import '../../less/_variables.less';

	@bg-weather: #0080ff;
	@severe-alert: #C00;

	.weather-mini {
		width: 55%;
		height: 40%;
		padding: 2% 1% 1% 2%;

		.weather-container {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			background-color: @bg-weather;
			border-radius: @border-radius;
			height: 100%;
			color: #fff;
			font-family: @font-main;
		}

		.conditions {
			display: flex;
			width: 100%;
			height: 50%;
			padding: 1.5em 1.5em 0;
		}

		.condition-img {
			flex: 0 0 16%;
			display: flex;
			align-items: center;

			img {
				width: 100%;
				height: auto;
			}
		}

		.condition-text {
			display: flex;
			justify-content: space-between;
			flex: 1 0 84%;
			padding-left: 1em;
		}

		.condition-temp {
			font-size: 5.5em;
			line-height: 1em;
			font-weight: 700;
		}

		.condition-detail {
			text-align: right;
			font-size: 1.4em;
			line-height: 1.3em;
			white-space: nowrap;

			span {
				display: block;
			}
		}

		.condition-temp-desc {
			overflow: hidden;
		}

		.condition-desc {
			width: 100%;
			font-size: 1.7em;
			font-weight: 700;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			max-width: 460px;
		}

		.severe-alert {
			padding: 0.2em;
			text-align: center;
			font-size: 1.5em;
			background-color: @severe-alert;
			width: 100%;
		}

		.forecast {
			display: flex;
			justify-content: space-between;
			width: 100%;
			height: 36%;
			padding: 0.5em 1em 1em;
		}

		.forecast-item {
			width: 17%;
			display: flex;
			flex-direction: column;

			h2 {
				font-size: 1.5em;
				margin: 0 0 0.4em;
				text-align: center;
				font-weight: 400;
				flex: 0 0 1em;
			}

			.forecast-detail {
				display: flex;
				align-items: center;
				justify-content: center;
				flex: 1 1 auto;
			}

			.forecast-img {
				flex: 0 0 40%;

				img {
					width: 100%;
					height: auto;
				}
			}

			.forecast-temps {
				flex: 0 0 auto;
				font-size: 1.4em;
				line-height: 1.3em;
				padding-left: 0.3em;
				text-align: right;

				span {
					display: block;
				}
			}

			.lo {
				opacity: 0.6;
			}
		}
	}
</style>


// WEBPACK FOOTER //
// weather-mini.vue?0735d005